// eslint-disable-next-line import/no-extraneous-dependencies
import { SyntheticEvent, useEffect, useMemo, useState } from "react"
import { SetURLSearchParams } from "react-router-dom"

import {
    MAX_CAPACITY,
    TOT_DEM_MAX_CAPACITY,
} from "../components/fitlerPanel/dnoFilterPanelFrame"
import { LCT_TILES } from "../components/submitApplication/single/selectLct/applicationTypeTiles"
import { EscalatedReasons } from "../constants/enums"
import {
    APPLICATION_ID_KW,
    ASSIGNED_TO,
    DNO_DATE_FROM_KW,
    DNO_DATE_TO_KW,
    DNO_DEFAULT_ORDER_BY,
    INSTALLER_DATE_FROM_KW,
    INSTALLER_DATE_TO_KW,
    ORDER_BY_TIMESTAMP,
    READY_FOR_CX,
    STATUS,
    TECHNOLOGY,
    POSTCODE,
    ADDRESS,
    ESCALATED_REASON,
    LAST_UPDATED,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    MAX_MAX_DEMAND,
    MIN_MAX_DEMAND,
    INSTALLER_EMAIL,
    IS_LOOPED_SUPPLY,
    IS_SUPPLY_ISSUES,
    ORDER_KW,
    IS_UNREAD,
    UPDATED_SINCE,
} from "../constants/generalConstants"
import useDnoUserSuggestion from "../services/middleware/hooks/dnoInterface/protected/useDnoUserSuggestion"

/**
 * Hook for deriving and managing filter panel input values from URL search
 * params for dno application search page. database search API.
 * Search params are treated as the filter panel state. This hook abstracts the
 * translation of this state into objects
 * the filter panel inputs can read.
 *
 * @param searchParams - URL search parameters
 * @param setSearchParams - setter function for URL search parameters
 * @returns - helper function for setting a filter value and values for all
 * database filter panel options.
 */
export const useApplicationDeriveFilters = (
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams,
    isDnoMode: boolean
): {
    applicationId: string
    clearAllFilters: () => void
    setFilterFromTextfield: (newValue: string, searchParam: string) => void
    orderBy: string
    orderDirection: string
    dateFrom: string
    dateTo: string
    updatedSince: string
    lastUpdated: string
    status: string | null
    assignedTo: string
    assignedToName: string
    readyForCx: boolean
    isLoopedSupply: boolean
    isSupplyIssues: boolean
    technology: string
    postcode: string
    address: string
    escalatedReason: string
    installerEmail: string
    setOrderBy: (orderBy: string, orderDirection: string) => void
    genCapacityMin: number
    genCapacityMax: number
    totDemCapacityMin: number
    totDemCapacityMax: number
    isUnread: boolean
    setFilterFromSlider: (
        e_: Event | SyntheticEvent<Element, Event>,
        newValue: number | number[],
        searchParam: string | string[]
    ) => void
} => {
    /**
     * Updates URL search params for filter panel elements which are text
     * @param newValue - new value being set
     * @param searchParam - url search param for the textfield
     */
    const setFilterFromTextfield = (
        newValue: string,
        searchParam: string
    ): void => {
        if (newValue) {
            searchParams.set(searchParam, String(newValue))
        } else {
            searchParams.delete(searchParam)
        }
        setSearchParams(new URLSearchParams(searchParams.toString()))
    }

    /**
     * Updates URL search params for filter panel elements which are sliders
     * @param e_ - Event object
     * @param newValue - new slider value (expected to be an array of [min, max])
     * @param searchParam - url search param key or array of keys [minParamKey, maxParamKey]
     */
    const setFilterFromSlider = (
        e_: Event | SyntheticEvent<Element, Event>,
        newValue: number | number[],
        searchParam: string | string[]
    ): void => {
        if (!Array.isArray(newValue)) {
            // TTR slider error: onSlideCommitted handle not returning array
            return
        }

        const [minValue, maxValue] = newValue as [number, number]

        if (Array.isArray(searchParam)) {
            // If searchParam is an array, use the first element for min value and second for max value
            const [minParamKey, maxParamKey] = searchParam

            // Clear existing parameters
            searchParams.delete(minParamKey)
            searchParams.delete(maxParamKey)

            // Only set params if values aren't at defaults
            if (minValue > 0) {
                searchParams.set(minParamKey, String(minValue))
            }

            if (maxValue < MAX_CAPACITY) {
                searchParams.set(maxParamKey, String(maxValue))
            }
        }

        setSearchParams(new URLSearchParams(searchParams.toString()))
    }

    /**
     * Sets order by and order direction URL params
     * @param orderBy - column to be sorted on
     * @param orderDirection - sort direction (asc | desc)
     */
    const setOrderBy = (orderBy: string, orderDirection: string): void => {
        searchParams.set(ORDER_KW, orderDirection)
        searchParams.set("sort_key", orderBy)
        setSearchParams(searchParams)
    }

    // Filter params
    const applicationId = searchParams.get(APPLICATION_ID_KW) || ""
    const dateFrom =
        searchParams.get(
            isDnoMode ? DNO_DATE_FROM_KW : INSTALLER_DATE_FROM_KW
        ) || ""
    const dateTo =
        searchParams.get(isDnoMode ? DNO_DATE_TO_KW : INSTALLER_DATE_TO_KW) ||
        ""
    const lastUpdated = searchParams.get(LAST_UPDATED) || ""
    const updatedSince = searchParams.get(UPDATED_SINCE) || ""
    const orderBy = ORDER_BY_TIMESTAMP
    const orderDirection =
        searchParams.get(ORDER_BY_TIMESTAMP) || DNO_DEFAULT_ORDER_BY
    const status = searchParams.get(STATUS) || null
    const postcode = searchParams.get(POSTCODE) || ""
    const address = searchParams.get(ADDRESS) || ""
    const installerEmail = searchParams.get(INSTALLER_EMAIL) || ""
    const escalatedReasonValue = searchParams.get(ESCALATED_REASON) || ""
    const escalatedReason = escalatedReasonValue
        ? EscalatedReasons.find((tile) => tile.value === escalatedReasonValue)
              ?.title || escalatedReasonValue
        : ""
    const technologyValue = searchParams.get(TECHNOLOGY) || ""
    const technology = technologyValue
        ? LCT_TILES.find((tile) => tile.value === technologyValue)?.title ||
          technologyValue
        : ""
    const readyForCx = searchParams.get(READY_FOR_CX) === "true"
    const isLoopedSupply = searchParams.get(IS_LOOPED_SUPPLY) === "true"
    const isSupplyIssues = searchParams.get(IS_SUPPLY_ISSUES) === "true"
    const assignedTo = searchParams.get(isDnoMode ? ASSIGNED_TO : "") || ""

    const [assignedToName, setAssignedToName] = useState<string>("")

    const dnoUserSuggestion = useDnoUserSuggestion("", false)
    const getUserNameFromId = useMemo(
        () =>
            isDnoMode
                ? dnoUserSuggestion.getUserNameFromId
                : (name: string): string => name,
        [isDnoMode, dnoUserSuggestion.getUserNameFromId]
    )
    useEffect(() => {
        const name = getUserNameFromId(assignedTo)
        setAssignedToName(name)
    }, [assignedTo, getUserNameFromId])

    const clearAllFilters = (): void => {
        setSearchParams(new URLSearchParams())
    }
    const genCapacityMin = Number(searchParams.get(MIN_GEN_CAPACITY)) || 0
    const genCapacityMax =
        Number(searchParams.get(MAX_GEN_CAPACITY)) || TOT_DEM_MAX_CAPACITY
    const totDemCapacityMax =
        Number(searchParams.get(MAX_MAX_DEMAND)) || TOT_DEM_MAX_CAPACITY
    const totDemCapacityMin = Number(searchParams.get(MIN_MAX_DEMAND)) || 0
    const isUnread = searchParams.get(IS_UNREAD) === "true"

    return {
        applicationId,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        lastUpdated,
        updatedSince,
        status,
        technology,
        postcode,
        address,
        escalatedReason,
        installerEmail,
        assignedTo,
        assignedToName,
        readyForCx,
        isLoopedSupply,
        isSupplyIssues,
        setOrderBy,
        setFilterFromTextfield,
        clearAllFilters,
        genCapacityMin,
        genCapacityMax,
        totDemCapacityMin,
        totDemCapacityMax,
        setFilterFromSlider,
        isUnread,
    }
}
